import type { ImageLoaderProps } from 'next/image';
import type { SwiperClass, SwiperRef } from 'swiper/react';
import Image from 'next/image';
import { sortBy } from '@poseidon/shared-utils';
import React, { useRef } from 'react';
import { Autoplay, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import { Arrows } from '@poseidon/components/StrokeIcons';
import { WaveSpinner } from '@poseidon/components/WaveSpinner';

import { isNotNull } from '@harmonizeai/poseidon-shared';
import { useClientSide } from '@/hooks/useClientSide';

type DocumentSlide = {
  id: string;
  webp: string | null;
  url: string;
  sizes: Array<{ w: number; url: string }> | null;
};

interface PDFViewerProps {
  images: DocumentSlide[];
  autoplay?: boolean;
}

export const PDFViewer = ({ images, autoplay }: PDFViewerProps) => {
  const ref = React.useRef<SwiperRef>(null);
  const progressCircle = useRef<SVGSVGElement>(null);
  const progressContent = useRef<HTMLSpanElement>(null);
  const onAutoplayTimeLeft = (s: SwiperClass, time: number, progress: number) => {
    if (progressCircle?.current) {
      progressCircle.current.style.setProperty('--progress', `${1 - progress}`);
    }
    if (progressContent?.current) {
      progressContent.current.textContent = `${Math.ceil(time / 1000)}s`;
    }
  };

  return (
    <Swiper
      ref={ref}
      className="w-full bg-(--card-background)"
      style={{ height: '100%' }}
      pagination={{ clickable: true }}
      modules={[autoplay ? Autoplay : undefined, Pagination].filter(isNotNull)}
      autoplay={
        autoplay
          ? {
              delay: 4000,
              disableOnInteraction: true,
            }
          : undefined
      }
      onAutoplayTimeLeft={autoplay ? onAutoplayTimeLeft : undefined}>
      {images.map((image, i) => (
        <SwiperSlide key={image.id}>
          <SwiperImage sizes={image.sizes ?? []} shouldPreload={i < 3} src={image.webp || image.url} />
        </SwiperSlide>
      ))}
      {images.length > 1 && (
        <button
          className="group absolute bottom-0 left-0 top-0 z-10 w-[100px] cursor-pointer border-none bg-transparent transition-all duration-200 ease-out hover:bg-black/25"
          onClick={() => ref.current?.swiper.slidePrev()}>
          <Arrows.ArrowLeft className="size-12 text-white transition-all duration-200 ease-out group-hover:scale-[1.3]" />
        </button>
      )}
      {images.length > 1 && (
        <button
          className="group absolute bottom-0 right-0 top-0 z-10 w-[100px] cursor-pointer border-none bg-transparent transition-all duration-200 ease-out hover:bg-black/25"
          onClick={() => ref.current?.swiper.slideNext()}>
          <Arrows.ArrowRight className="size-12 text-white transition-all duration-200 ease-out group-hover:scale-[1.3]" />
        </button>
      )}
      {images.length > 1 && (
        <div
          className="absolute bottom-4 right-4 z-10 flex size-12 items-center justify-center font-normal text-white"
          slot="container-end">
          <svg
            viewBox="0 0 48 48"
            ref={progressCircle}
            className="absolute left-0 top-0 z-10 size-full -rotate-90 fill-none stroke-[#ffffff48] stroke-[4]"
            style={{
              strokeDasharray: 125.6,
              strokeDashoffset: 'calc(125.6 * (1 - var(--progress, 0)))',
            }}>
            <circle cx="24" cy="24" r="20"></circle>
          </svg>
          <span ref={progressContent}></span>
        </div>
      )}
    </Swiper>
  );
};

type SwiperImageProps = {
  src: string;
  shouldPreload: boolean;
  sizes: Array<{ w: number; url: string }>;
};

const SwiperImage = ({ src, shouldPreload, sizes }: SwiperImageProps) => {
  const [isLoaded, setIsLoaded] = React.useState(false);
  const isClientSide = useClientSide();

  const imgProxyLoader = ({ src, width }: ImageLoaderProps) => {
    if (!sizes) return src;

    const sizesList = sortBy(sizes, (s) => s.w);

    // return the first size at least as big as asked
    for (const size of sizesList) {
      if (width <= size.w) return size.url;
    }
    return src;
  };

  return (
    <div className="relative h-full w-full bg-[#111] text-[#5149eb]">
      {!isLoaded && <WaveSpinner className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" />}
      <Image
        onLoad={() => setIsLoaded(true)}
        rel={shouldPreload ? 'preload' : 'lazy'}
        alt="pdf image"
        src={src}
        loader={imgProxyLoader}
        // hack to start load all the rest images on the browser when page is loaded
        priority={shouldPreload ? true : isClientSide ? true : undefined}
        fill
        style={{ objectFit: 'contain' }}
      />
    </div>
  );
};
